import { useContext, useEffect } from 'react'
import { useFormikContext } from 'formik'

import { EMPTY_LIST, EMPTY_OBJECT } from '../../../../../constants/common'
import { FORMIK_EMPTY_VALUE } from '../../../../../constants/validation'
import { useUserData } from '../../../../../hooks'
import { getItemsIds } from '../../../../../utils/common'
import { addSignature } from '../../../../../utils/signature'
import { EmailFormContext } from '../../../EmailForm.context'
import { getTemplate } from '../../EmailCreationForm.api'
import {
  EMAIL_CREATION_FORM_FIELD_NAME_FROM,
  EMAIL_CREATION_FORM_FIELD_NAME_INSERT_REPLACE,
  EMAIL_CREATION_FORM_FIELD_NAME_MESSAGE,
  EMAIL_CREATION_FORM_FIELD_NAME_REPLY_TEXT,
  EMAIL_CREATION_FORM_FIELD_NAME_SELECTED_TEMPLATE,
  EMAIL_CREATION_FORM_FIELD_NAME_SELECTED_TEMPLATE_FILES,
  EMAIL_CREATION_FORM_FIELD_NAME_SUBJECT,
  EMAIL_CREATION_FORM_FIELD_NAME_TEMPLATE,
} from '../../EmailCreationForm.constants'
import {
  isTemplateConfirmationInsertReplaceOptionValueInsert,
  isTemplateConfirmationInsertReplaceOptionValueReplace,
  TemplateConfirmationModal,
} from '../../TemplateConfirmationModal'
import { useSignature } from '../useSignature'

import { addTemplateValue, isDirtyMessage } from './useTemplate.utils'

export function useTemplate({
  addFiles,
  config = EMPTY_OBJECT,
  deleteFile,
  editorRef,
  params,
  templateEntityApiParams,
}) {
  const { setFieldValue, values } = useFormikContext()

  const user = useUserData()

  const { setModal, onCloseModal } = useContext(EmailFormContext)

  const { currentSignature } = useSignature()

  const { replyMode } = config

  const shouldUpdateSubject =
    !replyMode || (replyMode && !values[EMAIL_CREATION_FORM_FIELD_NAME_SUBJECT])

  function handleCloseModal() {
    setFieldValue(EMAIL_CREATION_FORM_FIELD_NAME_TEMPLATE, FORMIK_EMPTY_VALUE)
    onCloseModal()
  }

  function handleReplaceSubject({ subject }) {
    if (subject) {
      if (shouldUpdateSubject) {
        setFieldValue(EMAIL_CREATION_FORM_FIELD_NAME_SUBJECT, subject)
      }
    }
  }

  function handleReplaceFiles({ files }) {
    const lastFiles =
      values[EMAIL_CREATION_FORM_FIELD_NAME_SELECTED_TEMPLATE_FILES]

    if (lastFiles?.length > 0) {
      lastFiles.forEach(i => deleteFile(i))
    }

    addFiles(files)
    setFieldValue(
      EMAIL_CREATION_FORM_FIELD_NAME_SELECTED_TEMPLATE_FILES,
      getItemsIds(files)
    )
  }

  function handleReplaceMessage(data) {
    if (data.text) {
      setFieldValue(
        EMAIL_CREATION_FORM_FIELD_NAME_MESSAGE,
        addSignature({
          insertSignatureBeforeQuotedText:
            values[EMAIL_CREATION_FORM_FIELD_NAME_FROM]
              .insert_signature_before_quoted_text,
          message: replyMode
            ? data.text + values[EMAIL_CREATION_FORM_FIELD_NAME_REPLY_TEXT]
            : data.text,
          signatureHTML: currentSignature,
        })
      )
    }
  }

  function handleReplaceTemplateHistory(data, templateId) {
    setFieldValue(
      EMAIL_CREATION_FORM_FIELD_NAME_SELECTED_TEMPLATE,
      addTemplateValue({
        data,
        templateEntityApiParams,
        templateId,
        user,
      })
    )
  }

  const templateId = values[EMAIL_CREATION_FORM_FIELD_NAME_TEMPLATE]?.value

  function handleApplyTemplate(data) {
    handleReplaceSubject(data)
    handleReplaceTemplateHistory(data, templateId)
    handleReplaceMessage(data)
    handleReplaceFiles(data)
  }

  function handleSubmitModal(value) {
    if (
      isTemplateConfirmationInsertReplaceOptionValueInsert(
        value[EMAIL_CREATION_FORM_FIELD_NAME_INSERT_REPLACE]
      )
    ) {
      getTemplate(templateId, params).then(({ data }) => {
        if (data.subject && shouldUpdateSubject) {
          setFieldValue(
            EMAIL_CREATION_FORM_FIELD_NAME_SUBJECT,
            values[EMAIL_CREATION_FORM_FIELD_NAME_SUBJECT] + data.subject
          )
        }

        // first inserting content at the end of the editor if there is already content available
        if (!editorRef.current.isDirty() && data.text) {
          editorRef.current.focus()

          // event focus must end
          setTimeout(() => {
            editorRef.current.selection.select(
              editorRef.current.getBody(),
              true
            )
            editorRef.current.selection.collapse(false)
            editorRef.current.insertContent(data.text)
          }, 0)
        }

        if (editorRef.current.isDirty() && data.text) {
          editorRef.current.insertContent(data.text)
        }

        addFiles(data.files)
        setFieldValue(EMAIL_CREATION_FORM_FIELD_NAME_SELECTED_TEMPLATE_FILES, [
          ...(values[EMAIL_CREATION_FORM_FIELD_NAME_SELECTED_TEMPLATE_FILES] ||
            EMPTY_LIST),
          ...getItemsIds(data.files),
        ])

        handleReplaceTemplateHistory(data, templateId)
      })
    }

    if (
      isTemplateConfirmationInsertReplaceOptionValueReplace(
        value[EMAIL_CREATION_FORM_FIELD_NAME_INSERT_REPLACE]
      )
    ) {
      getTemplate(templateId, params).then(({ data }) => {
        handleApplyTemplate(data)
      })
    }

    handleCloseModal()
  }

  function setTemplateConfirmationModal() {
    setModal(
      <TemplateConfirmationModal
        onClose={handleCloseModal}
        onSubmit={handleSubmitModal}
      />
    )
  }

  const message = values[EMAIL_CREATION_FORM_FIELD_NAME_MESSAGE]

  const dirtyMessageField = isDirtyMessage(message, currentSignature)

  const dirtySubjectField =
    !replyMode && Boolean(values[EMAIL_CREATION_FORM_FIELD_NAME_SUBJECT])

  useEffect(() => {
    if (
      templateId &&
      (values[EMAIL_CREATION_FORM_FIELD_NAME_SELECTED_TEMPLATE]?.id ||
        dirtyMessageField)
    ) {
      setTemplateConfirmationModal()
    } else if (templateId && dirtySubjectField) {
      getTemplate(templateId, params).then(({ data }) => {
        if (data.subject) {
          setTemplateConfirmationModal()
        } else {
          setFieldValue(
            EMAIL_CREATION_FORM_FIELD_NAME_TEMPLATE,
            FORMIK_EMPTY_VALUE
          )

          handleApplyTemplate(data)
        }
      })
    } else if (templateId) {
      setFieldValue(EMAIL_CREATION_FORM_FIELD_NAME_TEMPLATE, FORMIK_EMPTY_VALUE)

      getTemplate(templateId, params).then(({ data }) => {
        handleApplyTemplate(data)
      })
    }
  }, [params, values[EMAIL_CREATION_FORM_FIELD_NAME_TEMPLATE]])
}
